import { useState } from 'react'
import { formatPhoneNumber } from '@/utils/contactForm'
import useCurrentPage from '@/hooks/use-current-page'
import useI18n from '@/hooks/use-i18n'

export type FormData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  birthday: string
  location: string
  language: string
  optInToTexts: boolean
}

export type FormErrors = Record<string, string>

export const useNewsletterForm = (onSuccess?: () => void) => {
  const { i18n } = useI18n()
  const { currentLanguage } = useCurrentPage()

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthday: '',
    location: '',
    language: currentLanguage as string,
    optInToTexts: false,
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)
  const [formSubmitSuccess, setFormSubmitSuccess] = useState(false)
  const [formSubmit, setFormSubmit] = useState(false)
  const [memberAlreadySubscribed, setMemberAlreadySubscribed] = useState(false)

  const handleChange = (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = event.target
    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }))

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: '',
      }))
    }
  }

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let formattedValue = formatPhoneNumber(event.target.value)

    // Additional formatting to ensure consistent display when user is typing
    const digits = formattedValue.replace(/\D/g, '')
    if (digits.length > 0) {
      // Only format once we have some digits
      if (digits.length <= 3) {
        formattedValue = digits
      } else if (digits.length <= 6) {
        formattedValue = `(${digits.substring(0, 3)}) ${digits.substring(3)}`
      } else {
        formattedValue = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`
      }
    }

    setFormData(prev => ({
      ...prev,
      phone: formattedValue,
    }))

    // Clear error when user starts typing
    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: '',
      }))
    }
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setFormData(prev => ({
      ...prev,
      optInToTexts: checked,
    }))
  }

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let value = event.target.value

    // Handle backspace and deletion logic
    if (value.length < formData.birthday.length) {
      if (value[value.length - 1] === '/') {
        value = value.slice(0, -1)
      }
      setFormData(prev => ({
        ...prev,
        birthday: value,
      }))
      return
    }

    // Only allow numbers
    value = value.replace(/\D/g, '').slice(0, 8)

    // Format with slashes
    if (value.length >= 4) {
      value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`
    } else if (value.length >= 2) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`
    }

    // Validate month (01-12)
    if (value.length >= 2) {
      const month = parseInt(value.slice(0, 2))
      if (month < 1 || month > 12) {
        value = ''
      }
    }

    // Validate day (01-31)
    if (value.length >= 5) {
      const day = parseInt(value.slice(3, 5))
      if (day < 1 || day > 31) {
        value = value.slice(0, 3)
      }
    }

    setFormData(prev => ({
      ...prev,
      birthday: value,
    }))

    if (errors.birthday) {
      setErrors(prev => ({
        ...prev,
        birthday: '',
      }))
    }
  }

  const formatBirthdayForAPI = (birthday: string) => {
    if (!birthday) return ''
    // convert from MM/DD/YYYY to DD/MM/YYYY
    const [month, day, year] = birthday.split('/')
    return `${day}/${month}/${year}`
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = i18n('required') || 'This field is required'
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = i18n('required') || 'This field is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = i18n('required') || 'This field is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = i18n('invalidEmail') || 'Please enter a valid email address'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = i18n('required') || 'This field is required'
    } else {
      // Check for proper US phone format - should have 10 digits after stripping formatting
      const phoneDigits = formData.phone.replace(/\D/g, '')
      if (phoneDigits.length !== 10) {
        newErrors.phone = i18n('invalidPhone') || 'Please enter a valid 10-digit phone number'
      }
    }
    if (!formData.birthday.trim()) {
      newErrors.birthday = i18n('required') || 'This field is required'
    }
    if (!formData.location) {
      newErrors.location = i18n('required') || 'This field is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      setFormSubmit(true)
      setFormSubmitSuccess(false)
      return
    }

    setLoading(true)
    try {
      const response = await fetch('/api/newsletter-sign-up', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          birthday: formatBirthdayForAPI(formData.birthday),
        }),
      })

      const data = await response.json()
      const memberExists = data?.message === 'Member Exists' || data?.message === 'You are already subscribed to this location'
      setMemberAlreadySubscribed(memberExists)
      setFormSubmitSuccess(data?.success && !memberExists)

      // Send GTM event
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'newsletter_submission',
      })

      // Call onSuccess callback if provided
      if (data?.success && !memberExists && onSuccess) {
        onSuccess()
      }
    } catch (error) {
      setMemberAlreadySubscribed(false)
      setFormSubmitSuccess(false)
      console.error('Newsletter signup failed:', error)
    }

    setFormSubmit(true)
    setLoading(false)
  }

  return {
    formData,
    errors,
    loading,
    formSubmitSuccess,
    formSubmit,
    memberAlreadySubscribed,
    handleChange,
    handlePhoneChange,
    handleCheckboxChange,
    handleDateChange,
    handleSubmit,
  }
}
